import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Cropper from "react-easy-crop";
import { cropPhoto } from "./adjudicatorApi";

// Frame a portrait (card 930d3ac0): drag to move, pinch, scroll or slide to
// zoom, inside the round frame event pages show. A full-screen sheet on a
// phone, a dialog on a wider screen. onDone(blob) gets the framed 1024px JPEG.
// A real modal: focus moves in, Tab stays inside, Escape cancels, the page
// behind is inert, and focus goes back where it was on close. Rendered on
// document.body (in the site's token scope) so the page can be made inert.
const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
const PhotoEditor = ({ src, initial, onDone, onCancel }) => {
  // Adjust reopens at the framing the judge chose (initial: { crop, zoom })
  const [crop, setCrop] = useState(initial?.crop || { x: 0, y: 0 });
  const [zoom, setZoom] = useState(initial?.zoom || 1);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const sheetRef = useRef(null);

  const onCropComplete = useCallback((_, pixels) => setArea(pixels), []);

  const busyRef = useRef(busy);
  busyRef.current = busy;
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;

  useEffect(() => {
    const sheet = sheetRef.current;
    const opener = document.activeElement;
    // everything else on the page: no clicks, no focus, hidden from readers
    const others = [...document.body.children].filter((el) => !el.contains(sheet));
    others.forEach((el) => el.setAttribute("inert", ""));
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    sheet.focus();
    const onKey = (e) => {
      if (e.key === "Escape" && !busyRef.current) cancelRef.current();
      if (e.key !== "Tab") return;
      const items = [...sheet.querySelectorAll(FOCUSABLE)];
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === sheet)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      others.forEach((el) => el.removeAttribute("inert"));
      document.body.style.overflow = overflow;
      if (opener && opener.focus) opener.focus();
    };
  }, []);

  const use = async () => {
    if (!area) return;
    setBusy(true);
    setError("");
    try {
      onDone(await cropPhoto(src, area), { crop, zoom });
    } catch (err) {
      setError(err instanceof Error ? err.message : "That photo couldn't be read.");
      setBusy(false);
    }
  };

  return createPortal(
    <div className="site adj adj-editor-host">
      <div className="adj-editor" role="dialog" aria-modal="true" aria-labelledby="adj-editor-title">
        <div className="adj-editor-sheet" ref={sheetRef} tabIndex={-1}>
          <h2 id="adj-editor-title" className="adj-editor-title">Frame your photo</h2>
          <p className="adj-editor-help">Drag to move. Pinch or use the slider to zoom.</p>
          <div className="adj-editor-stage">
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              minZoom={1}
              maxZoom={4}
              aspect={1}
              cropShape="round"
              showGrid={false}
              objectFit="cover"
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
          <label className="adj-editor-zoom">
            <span className="adj-sr">Zoom</span>
            <span aria-hidden="true">−</span>
            <input type="range" min={1} max={4} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
            <span aria-hidden="true">+</span>
          </label>
          {error && <p className="adj-error" role="alert">{error}</p>}
          <div className="adj-editor-acts">
            <button type="button" className="adj-btn ghost" onClick={onCancel} disabled={busy}>Cancel</button>
            <button type="button" className="adj-btn primary" onClick={use} disabled={busy || !area}>
              {busy ? "Saving…" : "Use photo"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PhotoEditor;
